/**
 * DepartmentTeamCards
 * Displays team performance cards within a department
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Grid,
  LinearProgress,
  Chip,
  Avatar,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Users,
  Target,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Clock,
  Activity,
  User,
} from 'lucide-react';

import { DepartmentTeamMetrics } from '@/types';

interface DepartmentTeamCardsProps {
  teams: DepartmentTeamMetrics[];
  isLoading: boolean;
  departmentName: string;
  showDetailedView?: boolean;
}

const DepartmentTeamCards: React.FC<DepartmentTeamCardsProps> = ({
  teams,
  isLoading,
  departmentName,
  showDetailedView = false,
}) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width={150} />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} md={4} key={i}>
                <Skeleton variant="rounded" height={200} />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  }

  if (teams.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">No teams data available</Typography>
        </CardContent>
      </Card>
    );
  }

  const getPerformanceColor = (rating: number) => {
    if (rating >= 4.5) return theme.palette.success.main;
    if (rating >= 4.0) return theme.palette.info.main;
    if (rating >= 3.5) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getUtilizationColor = (util: number) => {
    if (util >= 70 && util <= 100) return theme.palette.success.main;
    if (util > 100) return theme.palette.error.main;
    return theme.palette.warning.main;
  };

  const getVelocityIcon = (velocity: number) => {
    return velocity <= 1 ? TrendingUp : TrendingDown;
  };

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Users size={20} style={{ color: '#64748b' }} />
            <Typography variant="h6" fontWeight={600}>
              Teams in {departmentName}
            </Typography>
          </Box>
          <Chip 
            label={`${teams.length} Teams`} 
            size="small" 
            variant="outlined"
          />
        </Box>

        {/* Team Cards Grid */}
        <Grid container spacing={2}>
          {teams.map((team) => {
            const completionRate = team.activeTasks + team.completedTasks > 0
              ? Math.round((team.completedTasks / (team.activeTasks + team.completedTasks)) * 100)
              : 0;
            const VelocityIcon = getVelocityIcon(team.velocity);

            return (
              <Grid item xs={12} md={showDetailedView ? 6 : 4} key={team.teamId}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    height: '100%',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 2,
                    },
                  }}
                >
                  {/* Team Header */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: 'primary.main',
                      }}
                    >
                      <Users size={24} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {team.teamName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <User size={14} style={{ color: '#64748b' }} />
                        <Typography variant="caption" color="text.secondary">
                          {team.leadName}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={`${team.memberCount} members`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </Box>

                  {/* Quick Stats */}
                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          bgcolor: alpha(getPerformanceColor(team.avgPerformance), 0.08),
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Target size={14} style={{ color: getPerformanceColor(team.avgPerformance) }} />
                          <Typography variant="caption" color="text.secondary">
                            Performance
                          </Typography>
                        </Box>
                        <Typography 
                          variant="h6" 
                          fontWeight={700} 
                          sx={{ color: getPerformanceColor(team.avgPerformance) }}
                        >
                          {team.avgPerformance.toFixed(1)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          bgcolor: alpha(getUtilizationColor(team.avgUtilization), 0.08),
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Activity size={14} style={{ color: getUtilizationColor(team.avgUtilization) }} />
                          <Typography variant="caption" color="text.secondary">
                            Utilization
                          </Typography>
                        </Box>
                        <Typography 
                          variant="h6" 
                          fontWeight={700} 
                          sx={{ color: getUtilizationColor(team.avgUtilization) }}
                        >
                          {team.avgUtilization}%
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Task Progress */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Task Completion
                      </Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {completionRate}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={completionRate}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'grey.100',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3,
                          bgcolor: completionRate >= 70 ? 'success.main' : 
                                   completionRate >= 50 ? 'warning.main' : 'error.main',
                        },
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {team.completedTasks} completed
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {team.activeTasks} active
                      </Typography>
                    </Box>
                  </Box>

                  {/* Velocity */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      borderRadius: 1,
                      bgcolor: alpha(team.velocity <= 1 ? theme.palette.success.main : theme.palette.warning.main, 0.08),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <VelocityIcon 
                        size={14} 
                        style={{ 
                          color: team.velocity <= 1 ? theme.palette.success.main : theme.palette.warning.main 
                        }} 
                      />
                      <Typography variant="caption" color="text.secondary">
                        Velocity
                      </Typography>
                    </Box>
                    <Typography 
                      variant="body2" 
                      fontWeight={600}
                      sx={{ 
                        color: team.velocity <= 1 ? 'success.main' : 'warning.main' 
                      }}
                    >
                      {team.velocity.toFixed(2)}x
                    </Typography>
                  </Box>

                  {/* Detailed View Additional Info */}
                  {showDetailedView && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Quick Insights
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip
                          icon={<CheckCircle size={12} />}
                          label={`${completionRate >= 70 ? 'On Track' : 'Needs Attention'}`}
                          size="small"
                          color={completionRate >= 70 ? 'success' : 'warning'}
                          variant="outlined"
                          sx={{ fontSize: '0.65rem' }}
                        />
                        {team.avgUtilization > 100 && (
                          <Chip
                            icon={<Clock size={12} />}
                            label="Overloaded"
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem' }}
                          />
                        )}
                        {team.velocity <= 0.9 && (
                          <Chip
                            icon={<TrendingUp size={12} />}
                            label="High Velocity"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Summary Footer */}
        <Box 
          sx={{ 
            mt: 3, 
            pt: 2, 
            borderTop: 1, 
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total Members
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {teams.reduce((sum, t) => sum + t.memberCount, 0)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Avg Performance
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {(teams.reduce((sum, t) => sum + t.avgPerformance, 0) / teams.length).toFixed(1)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Avg Utilization
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {Math.round(teams.reduce((sum, t) => sum + t.avgUtilization, 0) / teams.length)}%
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Avg Velocity
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {(teams.reduce((sum, t) => sum + t.velocity, 0) / teams.length).toFixed(2)}x
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DepartmentTeamCards;
